// src/pages/Admin/Adherents/TableCompact.jsx
import React, { useMemo } from "react";
import ButtonPrint from "../../../components/Button/ButtonPrint";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import { sortIcon } from "./helpers";
import {
  getGroupPillClasses,
  isOutOfAllGroups,
  getSeasonRefDate,
  ageOn,
} from "./helpers";
import { ALL_COLUMNS } from "./columns.config";

export default function TableCompact({
  rows,
  totalCount,
  visibleCount,
  onScroll,
  scrollRef,
  sortOrder,
  toggleSortOrder,
  editingStatusId,
  setEditingStatusId,
  handleStatusChange,
  openChangeGroupModal,
  handleEditClick,
  handleDeleteClick,
  copyFilteredEmails,
  generateAdherentPdf,
  getStatusColor,
  activeSeason,
  visibleColumnIds, // <-- NOUVEAU
}) {
  // Colonnes visibles (hors identité & actions, fixes)
  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleColumnIds.includes(c.id)),
    [visibleColumnIds]
  );

  // Gabarit de colonnes CSS (identité + colonnes visibles + actions)
  const gridTemplateColumns = useMemo(() => {
    const dynamic = visibleColumns
      .map((c) => `minmax(${c.minWidth || 120}px, ${c.maxWidth || 1}fr)`)
      .join(" ");
    // Identité ~380px | dynamiques | Actions 150px
    return `380px ${dynamic} 150px`;
  }, [visibleColumns]);

  // Contexte passé aux renders de colonnes
  const ctx = {
    editingStatusId,
    setEditingStatusId,
    handleStatusChange,
    openChangeGroupModal,
    getGroupPillClasses,
    isOutOfAllGroups,
    getSeasonRefDate,
    ageOn,
    activeSeason,
    getStatusColor,
    copyFilteredEmails,
  };

  // Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="overflow-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 max-h-[72vh]"
    >
      <div className="min-w-[1024px]">
        {/* HEADER */}
        <div
          className="sticky top-0 z-30 grid bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur text-xs uppercase tracking-wide text-slate-700 dark:text-white"
          style={{ gridTemplateColumns }}
        >
          {/* Identité (sticky left) */}
          <div className="px-4 py-2 sticky left-0 bg-slate-50/90 dark:bg-slate-900/90 flex items-center gap-2">
            <span>Adhérent</span>
            <button
              onClick={toggleSortOrder}
              className="text-[11px] px-1 rounded hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-violet-400"
              title="Trier par âge"
            >
              {sortIcon(sortOrder)}
            </button>
          </div>

          {/* Colonnes dynamiques */}
          {visibleColumns.map((col) => (
            <div key={col.id} className="px-0 py-0">
              {col.renderHeader ? col.renderHeader(rows, ctx) : col.label}
            </div>
          ))}

          {/* Actions (sticky right) */}
          <div className="px-3 py-2 sticky right-0 bg-slate-50/90 dark:bg-slate-900/90">
            Actions
          </div>
        </div>

        {/* LIGNES */}
        {rows.map((a) => (
          <div
            key={a.id}
            className="grid items-center border-b border-slate-100 dark:border-slate-800 text-sm hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
            style={{ gridTemplateColumns }}
          >
            {/* Identité (sticky left) */}
            <div className="px-4 py-2 sticky left-0 bg-white/90 dark:bg-slate-900/40 flex items-center gap-3">
              <img
                className="rounded-full w-10 h-10 object-cover ring-1 ring-slate-200 dark:ring-slate-700 shrink-0"
                src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  // si l'image serveur ou URL fournie échoue, fallback front
                  e.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />
              <div className="min-w-0 leading-tight">
                <div className="text-slate-900 dark:text-slate-100 font-semibold tracking-tight truncate">
                  <span className="uppercase">{a.nom}</span>{" "}
                  <span className="capitalize font-medium">{a.prenom}</span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {a.licence_number || "—"}
                  {a.licence_number_jjb || "—"}
                </div>
              </div>
            </div>

            {/* Colonnes dynamiques */}
            {visibleColumns.map((col) => (
              <div key={col.id} className="px-0 py-0">
                {col.renderCell ? col.renderCell(a, ctx) : null}
              </div>
            ))}

            {/* Actions (sticky right) */}
            <div className="px-3 py-2 sticky right-0 bg-white/90 dark:bg-slate-900/40">
              <div className="flex items-center gap-2">
                <ButtonPrint onClick={() => generateAdherentPdf(a)} />
                <ButtonEdit onClick={() => handleEditClick(a)} />
                <ButtonDelete onClick={() => handleDeleteClick(a.id)} />
              </div>
            </div>
          </div>
        ))}

        {visibleCount < totalCount && (
          <div className="p-3 text-center text-xs text-slate-500">
            Affichage de {visibleCount} / {totalCount} — descendez pour charger la suite…
          </div>
        )}
      </div>
    </div>
  );
}
